import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
} from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import { PlacementArray } from "@ng-bootstrap/ng-bootstrap/util/positioning";
import { BrMaskModel } from "brmasker-ionic-3/dist/directives/brmasker-ionic-3";
import { NgbCustomParserFormatter } from "components/common/date-picker/ngb-custom-parser-formatter";
import { commonVar } from "providers/common-var/common";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import CredentialedResource from "../../../../models/resource-management/credentialed-resource";
import ProfessionalProvider from "../../../../models/resource-management/professional-provider";
import { ResourceManagementService } from "../../../../providers/resource-management/resource-management.service";
import { ContactDetails } from "../contact-details";

@Component({
  selector: "job-role-contact-details",
  templateUrl: "job-role-contact-details.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => JobRoleContactDetails),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => JobRoleContactDetails),
      multi: true,
    },
    { provide: NgbDateParserFormatter, useClass: NgbCustomParserFormatter },
  ],
})
export class JobRoleContactDetails extends ContactDetails {
  _contactIsEmployee: boolean = false;
  _contactIsSystemUser: boolean = false;
  _resourceType: number;
  npiDuplicated: boolean = false;
  hideAddDiscipline: boolean = false;
  npiShort: boolean = false;
  credDuplicated: boolean = false;
  invalidDate: boolean = false;

  stateList = commonVar.STATE_LIST;

  departmentOptions = [];
  positionOptions = [];
  credentialsOptions = [];
  disciplineOptions = [];
  employmentStatusOptions = [];
  allDisciplineOptions = [];
  volunteerDisciplineOptions = [
    {
      display: "Volunteer",
      value: 14,
    },
  ];
  volunteerTypeOptions = [];

  department = [
    "ADMISSIONS",
    "BEREAVEMENT",
    "BILLING",
    "CLINICAL",
    "COMPLIANCE",
    "FINANCIAL",
    "MARKETING",
    "OPERATIONS",
    "SOCIAL_SERVICES",
    "MEDICAL_RECORDS",
    "SPIRITUAL",
    "TECHNOLOGY",
    "ADMINISTRATION",
    "HUMAN_RESOURCES",
  ];
  position = [
    "AIDE",
    "ACCOUNTANT",
    "ADMINISTRATOR",
    "ASSISTANT_DIRECTOR",
    "CHAPLAIN",
    "COORDINATOR",
    "COUNSELOR",
    "DIRECTOR",
    "EXECUTIVE",
    "LIAISON",
    "MANAGER",
    "NURSE",
    "SOCIAL_WORKER",
    "SUPERVISOR",
    "THERAPIST",
    "MEDICAL_DIRECTOR",
    "BEREAVEMENT",
    "TECHNOLOGY",
    "MARKETING",
    "CLERK",
    "RECEPTIONIST",
    "LEAD",
  ];
  disciplinesName = [
    "LICENSED_NURSE",
    "NURSE_PRACTITIONER",
    "NUTRITIONIST",
    "OCCUPATIONAL_THERAPIST",
    "PHYSICAL_THERAPIST",
    "PHYSICIAN_ASSISTANT",
    "REGISTERED_NURSE",
    "SOCIAL_WORKER",
    "SPEECH_LANGUAGE_THERAPIST",
    "SPIRITUAL_ADVISOR",
    "BEREAVEMENT",
    "DOCTOR_OF_MEDICINE",
    "DOCTOR_OF_OSTEOPATHY",
    "HOSPICE_AIDE",
    "VOLUNTEER",
    "VOLUNTEER_COORDINATOR",
    "NO_DISCIPLINE",
  ];
  employmentStatus = ["FULL_TIME", "PART_TIME", "PRN"];

  datePlacementPreference: PlacementArray = ["bottom-left"];
  startDateMin: any = "";
  startDateMax: any = "";
  endDateMin: any = "";
  endDateMax: any = "";
  validFromMin: any = "";
  validFromMax: any = "";
  validToMin = [];
  validToMax: any = "";
  npiBrmasker: BrMaskModel;

  @Input() isEditMode: boolean = false;
  @Input() contactData: CredentialedResource;
  @Output() recheckNPIEvent = new EventEmitter<any>();
  @Output() deleteCredential = new EventEmitter<any>();

  get disciplines() {
    if (this.innerFormGroup.contains("disciplines")) {
      return this.innerFormGroup.get("disciplines") as FormArray;
    }
  }

  get credentials() {
    return this.innerFormGroup.get("credentials") as FormArray;
  }

  @Input() set contactIsEmployee(value: boolean) {
    this._contactIsEmployee = value;
    this._contactIsEmployee
      ? this.addEmployeeAttributes()
      : this.removeEmployeeAttributes();
  }

  get contactIsEmployee() {
    return this._contactIsEmployee;
  }

  @Input() set contactIsSystemUser(value: boolean) {
    this._contactIsSystemUser = value;
    this._contactIsSystemUser
      ? this.addSystemUserAttributes()
      : this.removeSystemUserAttributes();
  }

  get contactIsSystemUser() {
    return this._contactIsSystemUser;
  }

  @Input() set resourceType(value: number) {
    this._resourceType = value;
    this.addCompanyResourceAttributes();
    this.removeCompanyResourceAttributes();
    this.updateDisciplineOptions();
  }

  get resourceType() {
    return this._resourceType;
  }

  set value(value: any) {
    if (value.credentials) {
      const credentialsCount = value.credentials.length;
      let credentialsControlsCount = this.credentials.controls.length;

      while (credentialsControlsCount !== credentialsCount) {
        this.addCredential(event);
        credentialsControlsCount = this.credentials.controls.length;
      }
    }

    if (value.position || value.department || value.employmentStatus) {
      value.position = this.position.indexOf(value.position);
      value.department = this.department.indexOf(value.department);
      value.employmentStatus =
        this.employmentStatus.indexOf(value.employmentStatus) !== -1
          ? this.employmentStatus.indexOf(value.employmentStatus)
          : null;
      this.addEmployeeAttributes();
    }

    if (value.disciplines) {
      this.addSystemUserAttributes();

      // clean up disciplines - temp fix
      value.disciplines = value.disciplines.filter((x) => x !== null);

      const disciplinesCount = value.disciplines.length;
      let disciplinesControlsCount = this.disciplines.controls.length;

      while (disciplinesControlsCount !== disciplinesCount) {
        value.disciplines[disciplinesControlsCount].name =
          this.disciplinesName.indexOf(
            value.disciplines[disciplinesControlsCount].name
          );
        this.addDiscipline(event);
        disciplinesControlsCount = this.disciplines.controls.length;
      }
    }

    this.innerFormGroup.patchValue(value);
    this.onChange(value);
    this.onTouched();
  }

  constructor(
    protected formBuilder: FormBuilder,
    protected resourceManagementService: ResourceManagementService
  ) {
    super(formBuilder);

    this.innerFormGroup = this.formBuilder.group({
      credentials: new FormArray([]),
    });

    //Patch Select Value from RM Service
    this.resourceManagementService
      .getLookUpValues()
      .pipe()
      .take(1)
      .subscribe(
        (response) => {
          this.employmentStatus = response["EmploymentStatus"].map((item) => {
            return {
              display: item.description,
              value: item.id,
            };
          });

          this.credentialsOptions = response["CredentialType"].map((item) => {
            return {
              display: item.description,
              value: item.id,
            };
          });
          this.volunteerTypeOptions = response["VolunteerType"].map((item) => {
            return {
              display: item.description,
              value: item.id,
            };
          });
        },
        (err) => {}
      );

    //Patch Select Value from RM Service JobRoleDetails
    this.resourceManagementService
      .getJobRoleDetails()
      .pipe()
      .take(1)
      .subscribe(
        (response) => {
          this.departmentOptions = response["Department"].map((item) => {
            return {
              display: item.description,
              value: item.id - 1,
            };
          });
          this.positionOptions = response["Position"].map((item) => {
            return {
              display: item.description,
              value: item.id - 1,
            };
          });
          this.allDisciplineOptions = response["DisciplineName"].map((item) => {
            return {
              display: item.description,
              value: item.id - 1,
            };
          });
          this.disciplineOptions =
            this.disciplineOptions.length == 0
              ? this.allDisciplineOptions
              : this.disciplineOptions;
        },
        (err) => {}
      );

    this.subscriptions.push(
      this.innerFormGroup.valueChanges.subscribe((value) => {
        if (
          this.innerFormGroup.contains("npi") &&
          this.innerFormGroup.get("npi").dirty &&
          (value.npi || value.npi === "") &&
          value.npi.length < 10
        ) {
          this.npiShort = true;
        }
        this.onChange(value);
        this.onTouched();
      })
    );

    this.subscriptions.push(
      this.credentials.valueChanges.subscribe((value) => {
        let duplicated = false;
        let invalidDateCheck = false;
        let prevCred = {};
        for (let i = 0; i < this.credentials.value.length; i++) {
          let credential = this.credentials.at(i).value;
          if (!credential.deleted) {
            // Set limit for expiration date
            let fromDateValue = credential.validFromDatetime;
            let toDateValue = credential.validToDatetime;
            if (fromDateValue != "" && fromDateValue !== null) {
              let validFromDate = new Date(fromDateValue);
              this.validToMin[i] = {
                year: validFromDate.getFullYear(),
                month: validFromDate.getMonth() + 1,
                day: validFromDate.getDate(),
              };
            }
            //Validate credential date
            if (!toDateValue) {
              toDateValue = "";
            }
            if (toDateValue && toDateValue != "" && toDateValue !== null) {
              let validToDate = new Date(toDateValue);
              if (toDateValue > validToDate) invalidDateCheck = true;
            }
            // Duplicated credential management
            if (
              prevCred[credential.type] &&
              prevCred[credential.type][credential.issueState] &&
              credential.licenseId &&
              prevCred[credential.type][credential.issueState].includes(
                credential.licenseId
              )
            ) {
              duplicated = true;
            } else {
              credential.type = credential.type ? credential.type : 0;
              credential.issueState = credential.issueState
                ? credential.issueState
                : "";
              if (!prevCred[credential.type]) {
                prevCred[credential.type] = {};
              }
              if (!prevCred[credential.type][credential.issueState]) {
                prevCred[credential.type][credential.issueState] = [];
              }
              prevCred[credential.type][credential.issueState].push(
                credential.licenseId
              );
            }
            if (credential.type === 27 && !credential.other) {
              this.credentials
                .at(i)
                .get("other")
                .setValidators([Validators.required]);
            }
          }
        }
        this.credDuplicated = duplicated;
        this.invalidDate = invalidDateCheck;
      })
    );

    this.subscriptions.push(
      // Update Tab status with Async Validation
      this.innerFormGroup.statusChanges.subscribe((value) => {
        if (value === "VALID") {
          this.recheckNPIEvent.emit("");
        }
      })
    );

    let now = new Date();
    const tenYearsLater = {
      year: now.getFullYear() + 10,
      month: now.getMonth() + 1,
      day: now.getDate(),
    };
    const eightyYearsAgo = {
      year: now.getFullYear() - 80,
      month: now.getMonth() + 1,
      day: now.getDate(),
    };

    this.startDateMin = eightyYearsAgo;
    this.startDateMax = tenYearsLater;
    this.endDateMin = eightyYearsAgo;
    this.endDateMax = tenYearsLater;
    this.validFromMin = eightyYearsAgo;
    this.validFromMax = tenYearsLater;
    this.validToMax = tenYearsLater;
    this.npiBrmasker = new BrMaskModel();
    this.npiBrmasker.len = 10;
    this.npiBrmasker.type = "num";
  }

  valid(control: string) {
    return this.innerFormGroup.get(control).valid;
  }

  touched(control: string) {
    return this.innerFormGroup.get(control).touched;
  }

  validCredential(index) {
    return this.credentials.at(index).valid;
  }

  touchedCredential(index) {
    return this.credentials.at(index).touched;
  }

  validate(_: FormControl) {
    return this.innerFormGroup.valid &&
      !this.credDuplicated &&
      !this.invalidDate
      ? null
      : { jobRole: { valid: false } };
  }

  // Discipline
  addDiscipline(event) {
    event.preventDefault();
    if (this.disciplines.value.length === 0) {
      let defaultValue =
        this.resourceType == 5
          ? this.volunteerDisciplineOptions[0].value
          : null;
      const group = this.formBuilder.group({
        name: [defaultValue, Validators.required],
        startDate: [""],
        endDate: [""],
      });
      this.disciplines.push(group);
    }
    this.hideAddDiscipline = true;
  }

  removeDiscipline(event, index) {
    event.preventDefault();
    this.disciplines.removeAt(index);
    this.hideAddDiscipline = false;
  }

  updateDisciplineOptions() {
    if (this.disciplines && this.disciplines.controls.length == 1) {
      this.disciplines
        .at(0)
        .get("name")
        .setValue(
          this.resourceType == 5
            ? this.volunteerDisciplineOptions[0].value
            : null
        );
    }
    this.disciplineOptions =
      this.resourceType === 5
        ? this.volunteerDisciplineOptions
        : this.allDisciplineOptions;
  }

  // Credential
  addCredential(event) {
    event.preventDefault();
    const group = this.formBuilder.group({
      id: [""],
      type: [null, Validators.required],
      other: [null],
      issueState: [null, Validators.required],
      licenseId: ["", Validators.required],
      validFromDatetime: ["", Validators.required],
      validToDatetime: [""],
      operatingAs: [true],
      deleted: [""],
    });

    let now = new Date();
    const today = {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
    };
    this.validToMin.push(today);
    this.credentials.push(group);
  }

  removeCredential(event, index, credential) {
    event.preventDefault();
    if (credential.value["id"] > 0) {
      credential.get("deleted").setValue(true);
      credential.get("type").clearValidators();
      credential.get("licenseId").clearValidators();
      credential.get("issueState").clearValidators();
      credential.get("validFromDatetime").clearValidators();
      credential.get("validToDatetime").clearValidators();
      credential.get("other").clearValidators();
      this.deleteCredential.emit(credential.value);
    }
    this.validToMin.splice(index, 1);
    this.credentials.removeAt(index);
  }

  filterDeletedCredentials(creds: AbstractControl[]): AbstractControl[] {
    return creds.filter(
      (c) => c.value["deleted"] == false || c.value["deleted"] == ""
    );
  }

  // Employee
  addEmployeeAttributes() {
    if (!this.innerFormGroup.contains("department")) {
      this.innerFormGroup.addControl(
        "department",
        this.formBuilder.control(null, Validators.required)
      );
    }
    if (!this.innerFormGroup.contains("position")) {
      this.innerFormGroup.addControl(
        "position",
        this.formBuilder.control(null, Validators.required)
      );
    }
    if (!this.innerFormGroup.contains("employmentStatus")) {
      this.innerFormGroup.addControl(
        "employmentStatus",
        this.formBuilder.control(null)
      );
    }
  }

  removeEmployeeAttributes() {
    if (this.innerFormGroup.contains("department")) {
      this.innerFormGroup.removeControl("department");
    }
    if (this.innerFormGroup.contains("position")) {
      this.innerFormGroup.removeControl("position");
    }
    if (this.innerFormGroup.contains("employmentStatus")) {
      this.innerFormGroup.removeControl("employmentStatus");
    }

    if (this.resourceType === 1 && this.innerFormGroup.contains("npi")) {
      this.innerFormGroup.controls["npi"].setValue(
        this.innerFormGroup.get("npi").value
      );
    }
  }

  addSystemUserAttributes() {
    if (!this.innerFormGroup.contains("disciplines")) {
      this.innerFormGroup.addControl("disciplines", this.formBuilder.array([]));
    }
  }

  removeSystemUserAttributes() {
    if (this.innerFormGroup.contains("disciplines")) {
      this.innerFormGroup.removeControl("disciplines");
    }
    this.hideAddDiscipline = false;
  }

  // Company Resource (Provider vs Clinician vs Volunteer)
  addCompanyResourceAttributes() {
    if (this.resourceType === 1) {
      if (!this.innerFormGroup.contains("npi")) {
        this.innerFormGroup.addControl(
          "npi",
          this.formBuilder.control(
            "",
            [Validators.required, Validators.pattern("^[0-9]{10}$")],
            this.validateProviderAvailable.bind(this)
          )
        );
      }
      if (!this.innerFormGroup.contains("dea")) {
        this.innerFormGroup.addControl(
          "dea",
          this.formBuilder.control("", [
            Validators.minLength(9),
            Validators.pattern("^[a-zA-Z]{2}[0-9]{7}$"),
          ])
        );
      }
      if (!this.innerFormGroup.contains("taxId")) {
        this.innerFormGroup.addControl("taxId", this.formBuilder.control(""));
      }
      if (!this.innerFormGroup.contains("taxonomyCode")) {
        this.innerFormGroup.addControl(
          "taxonomyCode",
          this.formBuilder.control("")
        );
      }
    } else if (this.resourceType === 5) {
      if (!this.innerFormGroup.contains("volunteerType")) {
        this.innerFormGroup.addControl(
          "volunteerType",
          this.formBuilder.control(null, Validators.required)
        );
      }
      if (!this.innerFormGroup.contains("trainingCompletionDate")) {
        this.innerFormGroup.addControl(
          "trainingCompletionDate",
          this.formBuilder.control("")
        );
      }
    }
  }

  removeCompanyResourceAttributes() {
    if (this.resourceType !== 1) {
      if (this.innerFormGroup.contains("npi")) {
        this.innerFormGroup.removeControl("npi");
      }
      if (this.innerFormGroup.contains("dea")) {
        this.innerFormGroup.removeControl("dea");
      }
      if (this.innerFormGroup.contains("taxId")) {
        this.innerFormGroup.removeControl("taxId");
      }
      if (this.innerFormGroup.contains("taxonomyCode")) {
        this.innerFormGroup.removeControl("taxonomyCode");
      }
      this.npiDuplicated = false;
      this.npiShort = false;
    }
    if (this.resourceType !== 5) {
      if (this.innerFormGroup.contains("volunteerType")) {
        this.innerFormGroup.removeControl("volunteerType");
      }
      if (this.innerFormGroup.contains("trainingCompletionDate")) {
        this.innerFormGroup.removeControl("trainingCompletionDate");
      }
    }
  }

  validateProviderAvailable(
    control: AbstractControl
  ): Observable<ValidationErrors | null> {
    return this.resourceManagementService
      .checkDuplicateProvider(control.value)
      .pipe(
        map((res) => {
          // check if self
          this.npiShort = false;
          if (this.contactData) {
            let p = this.contactData as ProfessionalProvider;
            if (control.value === p.npi) {
              return null;
            }
          }
          this.npiDuplicated = this.innerFormGroup.contains("npi") && res;
          return !res ? null : { npiTaken: true };
        })
      );
  }
}



// WEBPACK FOOTER //
// ./src/pages/company-resources/contact-details/job-role/job-role-contact-details.ts